import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, apiPut } from '@/lib/api';
import type { OrganizationInvitation, OrganizationMember } from '@/types/organizations';

export interface OrganizationMemberWithUser extends OrganizationMember {
  email?: string;
  name?: string | null;
  avatar_url?: string | null;
}

export interface PendingInvitationApi {
  id: string;
  organization_id: string;
  email: string;
  role: OrganizationInvitation['role'];
  status: OrganizationInvitation['status'];
  invited_by: string;
  invited_at: string;
  organization_name: string;
  organization_slug: string;
}

export const organizationMembersKeys = {
  all: ['organization-members'] as const,
  members: (orgId: string) => [...organizationMembersKeys.all, orgId, 'members'] as const,
  invitationsMe: () => [...organizationMembersKeys.all, 'invitations-me'] as const,
  pendingInvitations: (orgId: string) =>
    [...organizationMembersKeys.all, orgId, 'pending-invitations'] as const,
};

export const listOrganizationMembers = (orgId: string) =>
  apiGet<OrganizationMemberWithUser[]>(`/organizations/${orgId}/members`);

export const inviteOrganizationMember = (
  orgId: string,
  payload: { email: string; role: 'admin' | 'member' | 'coach' | 'player' },
) => apiPost<OrganizationInvitation>(`/organizations/${orgId}/members/invite`, payload);

export const acceptOrganizationInvitation = (orgId: string, invitationId: string) =>
  apiPost<OrganizationMember>(`/organizations/${orgId}/invitations/${invitationId}/accept`);

export const removeOrganizationMember = (orgId: string, memberId: string) =>
  apiDelete<{ deleted: boolean }>(`/organizations/${orgId}/members/${memberId}`);

export const updateOrganizationMemberRole = (
  orgId: string,
  memberId: string,
  role: 'owner' | 'admin' | 'member' | 'coach' | 'player',
) =>
  apiPut<OrganizationMember>(`/organizations/${orgId}/members/${memberId}/role`, {
    role,
  });

export const cancelOrganizationInvitation = (orgId: string, invitationId: string) =>
  apiDelete<{ deleted: boolean }>(`/organizations/${orgId}/invitations/${invitationId}`);

export const listMyInvitations = () =>
  apiGet<PendingInvitationApi[]>('/organizations/invitations/me');

export const listOrganizationPendingInvitations = (orgId: string) =>
  apiGet<OrganizationInvitation[]>(`/organizations/${orgId}/invitations`);

export const useOrganizationMembersQuery = (orgId?: string, enabled = true) =>
  useQuery({
    queryKey: organizationMembersKeys.members(orgId || 'none'),
    queryFn: () => listOrganizationMembers(orgId as string),
    enabled: !!orgId && enabled,
  });

export const useMyInvitationsQuery = (enabled = true) =>
  useQuery({
    queryKey: organizationMembersKeys.invitationsMe(),
    queryFn: listMyInvitations,
    enabled,
  });

export const useOrganizationPendingInvitationsQuery = (orgId?: string, enabled = true) =>
  useQuery({
    queryKey: organizationMembersKeys.pendingInvitations(orgId || 'none'),
    queryFn: () => listOrganizationPendingInvitations(orgId as string),
    enabled: !!orgId && enabled,
  });

export const useInviteOrganizationMemberMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { email: string; role: 'admin' | 'member' | 'coach' | 'player' }) =>
      inviteOrganizationMember(orgId as string, payload),
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({
          queryKey: organizationMembersKeys.members(orgId),
        });
        void queryClient.invalidateQueries({
          queryKey: organizationMembersKeys.pendingInvitations(orgId),
        });
      }
    },
  });
};

export const useAcceptInvitationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, invitationId }: { orgId: string; invitationId: string }) =>
      acceptOrganizationInvitation(orgId, invitationId),
    onSuccess: (_member, variables) => {
      void queryClient.invalidateQueries({
        queryKey: organizationMembersKeys.members(variables.orgId),
      });
      void queryClient.invalidateQueries({
        queryKey: organizationMembersKeys.invitationsMe(),
      });
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
};

export const useRemoveOrganizationMemberMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => removeOrganizationMember(orgId as string, memberId),
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({
          queryKey: organizationMembersKeys.members(orgId),
        });
      }
    },
  });
};

export const useUpdateOrganizationMemberRoleMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      memberId,
      role,
    }: {
      memberId: string;
      role: 'owner' | 'admin' | 'member' | 'coach' | 'player';
    }) => updateOrganizationMemberRole(orgId as string, memberId, role),
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({
          queryKey: organizationMembersKeys.members(orgId),
        });
      }
    },
  });
};

export const useCancelOrganizationInvitationMutation = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      cancelOrganizationInvitation(orgId as string, invitationId),
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({
          queryKey: organizationMembersKeys.pendingInvitations(orgId),
        });
      }
    },
  });
};
